import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getTierConfig, trialDaysRemaining, userHasFeature } from '../constants/tiers';
import { getAdConfig, getSubscriptionStatus } from '../services/api';
import CurrencySelector from './CurrencySelector';
import NotificationBell from './NotificationBell';
import UpgradeModal from './UpgradeModal';
import InstallPrompt, { OfflineBanner } from './InstallPrompt';
import {
  LayoutDashboard,
  Bot,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Shield,
  ExternalLink,
  TrendingUp,
  Brain,
  Zap,
  Wrench,
  Bell,
  Download,
  Globe,
  Store,
  Crown,
  DollarSign,
  HelpCircle,
  Copy,
  FlaskConical,
  Gift,
  ShieldAlert,
  AlertTriangle,
  Lock,
  Activity,
  MessageSquare,
  CalendarRange,
  HeartPulse,
  Droplets,
  ClipboardCheck,
  Radar,
  FileText,
  Sparkles,
  ChevronRight,
  Hash,
  ArrowDownUp,
  Wallet,
  Layers,
  BookOpen,
  Radio,
  Grid3X3,
  BadgeCheck,
  GitBranch,
  Skull,
  TrendingDown,
  Trophy,
  Calculator,
  Newspaper,
  Coins,
  Webhook,
  Scale,
  ShieldCheck,
  Target,
  Receipt,
  Gem,
  Building2,
  Compass,
  Users,
} from 'lucide-react';
import logo from '../assets/images/haythix-logo.png';

// ─── Live Price Ticker ────────────────────────────────────────────────────────

const TICKER_COINS = [
  { id: 'bitcoin',       symbol: 'BTC',  name: 'Bitcoin'   },
  { id: 'ethereum',      symbol: 'ETH',  name: 'Ethereum'  },
  { id: 'solana',        symbol: 'SOL',  name: 'Solana'    },
  { id: 'binancecoin',   symbol: 'BNB',  name: 'BNB'       },
  { id: 'matic-network', symbol: 'POL',  name: 'Polygon'   },
  { id: 'arbitrum',      symbol: 'ARB',  name: 'Arbitrum'  },
  { id: 'avalanche-2',   symbol: 'AVAX', name: 'Avalanche' },
  { id: 'chainlink',     symbol: 'LINK', name: 'Chainlink' },
  { id: 'uniswap',       symbol: 'UNI',  name: 'Uniswap'   },
  { id: 'aave',          symbol: 'AAVE', name: 'Aave'      },
  { id: 'base',          symbol: 'BASE', name: 'Base'      },
  { id: 'tether',        symbol: 'USDT', name: 'Tether'    },
];

const FALLBACK = {
  bitcoin:       { usd: 67420,  usd_24h_change:  1.24 },
  ethereum:      { usd: 3512,   usd_24h_change:  0.87 },
  solana:        { usd: 172.3,  usd_24h_change:  2.41 },
  binancecoin:   { usd: 598.1,  usd_24h_change: -0.33 },
  'matic-network':{ usd: 0.912, usd_24h_change:  1.05 },
  arbitrum:      { usd: 1.14,   usd_24h_change: -0.72 },
  'avalanche-2': { usd: 38.7,   usd_24h_change:  1.89 },
  chainlink:     { usd: 17.42,  usd_24h_change:  0.56 },
  uniswap:       { usd: 10.83,  usd_24h_change: -1.12 },
  aave:          { usd: 212.4,  usd_24h_change:  2.03 },
  base:          { usd: 1.00,   usd_24h_change:  0.00 },
  tether:        { usd: 1.00,   usd_24h_change:  0.01 },
};

function formatPrice(price) {
  if (price >= 1000)  return price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  if (price >= 1)     return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return price.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 4 });
}

function PriceTicker() {
  const [data, setData] = useState(FALLBACK);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchPrices = async () => {
    try {
      const ids = TICKER_COINS.map(c => c.id).join(',');
      const res = await fetch(
        `https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=usd&include_24hr_change=true`,
        { signal: AbortSignal.timeout(8000) }
      );
      if (!res.ok) return;
      const json = await res.json();
      setData(prev => ({ ...prev, ...json }));
      setLastUpdated(new Date());
    } catch {
      // keep previous data on error
    }
  };

  useEffect(() => {
    fetchPrices();
    const interval = setInterval(fetchPrices, 60000);
    return () => clearInterval(interval);
  }, []);

  const items = TICKER_COINS.map(coin => ({
    ...coin,
    price:  data[coin.id]?.usd,
    change: data[coin.id]?.usd_24h_change,
  })).filter(c => c.price != null);

  if (!items.length) return null;

  const track = [...items, ...items];

  return (
    <div
      className="relative h-8 bg-dark-900 border-b border-dark-700 overflow-hidden flex items-center select-none"
      title={lastUpdated ? `Last updated: ${lastUpdated.toLocaleTimeString()}` : 'Loading live prices…'}
    >
      <style>{`
        @keyframes ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .ticker-track {
          display: flex;
          width: max-content;
          animation: ticker-scroll 70s linear infinite;
        }
        .ticker-track:hover {
          animation-play-state: paused;
        }
      `}</style>

      <div className="absolute left-0 top-0 h-full flex items-center z-10 pr-3 pl-2 bg-gradient-to-r from-dark-900 via-dark-900 to-transparent pointer-events-none">
        <span className="flex items-center gap-1.5 text-xs font-bold tracking-widest text-accent-primary">
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-primary opacity-75" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-accent-primary" />
          </span>
          LIVE
        </span>
      </div>

      <div className="ticker-track pl-16">
        {track.map((coin, i) => (
          <div key={`${coin.id}-${i}`} className="flex items-center gap-1.5 px-4 whitespace-nowrap">
            <span className="text-gray-500 text-xs font-semibold">{coin.symbol}</span>
            <span className="text-white text-xs font-mono font-semibold">${formatPrice(coin.price)}</span>
            <span className={`text-xs font-semibold flex items-center gap-0.5 ${coin.change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {coin.change >= 0 ? '▲' : '▼'}{Math.abs(coin.change).toFixed(2)}%
            </span>
            <span className="text-dark-600 text-xs ml-1">·</span>
          </div>
        ))}
      </div>

      <div className="absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-dark-900 to-transparent pointer-events-none" />
    </div>
  );
}

// ─── Ad Units ─────────────────────────────────────────────────────────────────

// Ad specs: Header 728×90 · from $500/day · Sidebar 300×250 · from $300/day
// Contact: info@haythix.com

const ACCENT_HEX = {
  amber:   '#f59e0b',
  violet:  '#8b5cf6',
  emerald: '#10b981',
  blue:    '#3b82f6',
  rose:    '#f43f5e',
};

const DEFAULT_AD_CONFIG = {
  header: { enabled: false, imageUrl: '', linkUrl: '', headline: 'Advertise Here', subText: 'Reach thousands of active DeFi traders', cta: 'Learn More' },
  slots: [
    { id: 'slot1', enabled: false, tag: 'Sponsored', imageUrl: '', linkUrl: '', headline: 'Your Ad Here', body: '300×250 · Premium placement', cta: 'Learn More', accentColor: 'amber' },
    { id: 'slot2', enabled: false, tag: 'Sponsored', imageUrl: '', linkUrl: '', headline: 'Your Ad Here', body: '300×250 · Prime sidebar spot', cta: 'Get Started', accentColor: 'violet' },
    { id: 'slot3', enabled: false, tag: 'Sponsored', imageUrl: '', linkUrl: '', headline: 'Your Ad Here', body: '300×250 · High-visibility slot', cta: 'Contact Us', accentColor: 'emerald' },
    { id: 'slot4', enabled: false, tag: 'Sponsored', imageUrl: '', linkUrl: '', headline: 'Your Ad Here', body: '300×250 · Premium below-fold spot', cta: 'Learn More', accentColor: 'cyan' },
    { id: 'slot5', enabled: false, tag: 'Sponsored', imageUrl: '', linkUrl: '', headline: 'Your Ad Here', body: '300×250 · Last inventory slot', cta: 'Get Started', accentColor: 'rose' },
  ]
};

// Merge a saved ad config with DEFAULT_AD_CONFIG so newly-added slot IDs
// (e.g. slot4, slot5 added 2026-06-05) show up immediately for users whose
// saved config was written when only 3 slots existed. Without this, an old
// `{ slots: [slot1, slot2, slot3] }` blob in storage overrides the 5-slot
// default and the new inventory is invisible until someone re-saves.
function mergeAdConfig(saved) {
  if (!saved) return DEFAULT_AD_CONFIG;
  const savedSlotsById = new Map((saved.slots || []).map(s => [s.id, s]));
  const mergedSlots = DEFAULT_AD_CONFIG.slots.map(def => savedSlotsById.get(def.id) || def);
  return {
    header: saved.header || DEFAULT_AD_CONFIG.header,
    slots: mergedSlots,
  };
}

// Normalize ad destination URLs: fix typos and strip accidental base-URL prefixes.
function cleanAdUrl(url) {
  if (!url || url === '#') return url;
  // Fix protocol typos: https;// or https;/ → https://
  let clean = url.replace(/^(https?);\/?\/?/, '$1://');
  // Strip accidental base-URL prefix (e.g. "https://storage.../https://site.com")
  const i = clean.indexOf('https://', 8);
  if (i > 0) return clean.slice(i);
  const j = clean.indexOf('http://', 7);
  if (j > 0) return clean.slice(j);
  return clean;
}

function HeaderAdBanner({ config }) {
  const header = config?.header || DEFAULT_AD_CONFIG.header;
  const isExpired = header.expiresAt && new Date(header.expiresAt) < new Date();
  const effectiveEnabled = header.enabled && !isExpired;

  if (effectiveEnabled && header.imageUrl) {
    return (
      <a
        href={cleanAdUrl(header.linkUrl) || '#'}
        target="_blank"
        rel="noopener noreferrer"
        className="hidden lg:flex flex-1 items-center justify-center mx-4 max-w-2xl overflow-hidden rounded-lg"
      >
        <img src={header.imageUrl} alt={header.headline} className="h-10 max-w-full object-contain" />
      </a>
    );
  }

  if (effectiveEnabled) {
    return (
      <a
        href={cleanAdUrl(header.linkUrl) || 'mailto:info@haythix.com'}
        target={header.linkUrl ? '_blank' : undefined}
        rel="noopener noreferrer"
        className="hidden lg:flex flex-1 items-center gap-3 mx-4 px-4 py-2 rounded-lg
                   bg-gradient-to-r from-violet-950/60 to-indigo-950/60
                   border border-violet-700/20 hover:border-violet-500/40
                   transition-all group cursor-pointer max-w-2xl"
      >
        <span className="text-[9px] font-bold tracking-widest text-violet-400 bg-violet-500/15 px-1.5 py-0.5 rounded flex-shrink-0 uppercase">Ad</span>
        <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors truncate min-w-0">
          <span className="text-white font-semibold">{header.headline}</span>
          {header.subText && <> — <span className="text-violet-300">{header.subText}</span></>}
        </span>
        <span className="text-xs font-semibold text-violet-400 group-hover:text-violet-200 flex-shrink-0 transition-colors">
          {header.cta} →
        </span>
      </a>
    );
  }

  // Placeholder — no active ad
  return (
    <Link
      to="/advertise"
      className="hidden lg:flex flex-1 items-center gap-3 mx-4 px-4 py-2 rounded-lg
                 bg-gradient-to-r from-violet-950/60 to-indigo-950/60
                 border border-violet-700/20 hover:border-violet-500/40
                 transition-all group cursor-pointer max-w-2xl"
    >
      <span className="text-[9px] font-bold tracking-widest text-violet-400 bg-violet-500/15 px-1.5 py-0.5 rounded flex-shrink-0 uppercase">Ad</span>
      <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors truncate min-w-0">
        Reach <span className="text-white font-semibold">active crypto traders</span>
        {' — '}
        <span className="text-violet-300">Leaderboard Banner · 728×90 · from $500/day</span>
      </span>
      <span className="text-xs font-semibold text-violet-400 group-hover:text-violet-200 flex-shrink-0 transition-colors">Advertise →</span>
    </Link>
  );
}

function SidebarAdUnit({ slot }) {
  const accent = ACCENT_HEX[slot.accentColor] || ACCENT_HEX.amber;
  const isExpired = slot.expiresAt && new Date(slot.expiresAt) < new Date();

  if (!slot.enabled || isExpired) {
    return (
      <Link to="/advertise"
        className="block rounded-xl bg-gradient-to-br from-dark-800 to-dark-700 border border-dashed border-dark-500 hover:brightness-110 transition-all"
        style={{ minHeight: '180px' }}>
        <div className="p-4 flex flex-col h-full min-h-[180px]">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[9px] font-bold tracking-widest text-amber-400/80 uppercase">ADVERTISE HERE</span>
            <span className="text-[9px] text-gray-500 font-mono">300×250</span>
          </div>
          <p className="font-bold text-sm leading-snug mb-1.5" style={{ color: accent }}>Your Ad Here</p>
          <p className="text-gray-500 text-xs leading-relaxed flex-1">Reach active crypto traders<br />From $300/day · 24h minimum</p>
          <div className="mt-3">
            <span className="inline-block text-xs font-semibold px-3 py-1.5 rounded-lg" style={{ backgroundColor: `${accent}18`, color: accent, border: `1px solid ${accent}35` }}>
              Book This Slot →
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (slot.imageUrl) {
    return (
      <a href={cleanAdUrl(slot.linkUrl) || '#'} target="_blank" rel="noopener noreferrer"
        className="block rounded-xl overflow-hidden hover:brightness-110 transition-all"
        style={{ minHeight: '180px' }}>
        <img src={slot.imageUrl} alt={slot.headline} className="w-full object-cover" style={{ minHeight: '180px' }} />
      </a>
    );
  }

  return (
    <a href={cleanAdUrl(slot.linkUrl) || 'mailto:info@haythix.com'} target={slot.linkUrl ? '_blank' : undefined} rel="noopener noreferrer"
      className="block rounded-xl bg-dark-700 border hover:brightness-110 transition-all"
      style={{ minHeight: '180px', borderColor: `${accent}30` }}>
      <div className="p-4 flex flex-col h-full min-h-[180px]">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[9px] font-bold tracking-widest text-gray-500 uppercase">{slot.tag || 'Sponsored'}</span>
          <span className="text-[9px] text-gray-600 font-mono">300×250</span>
        </div>
        <p className="font-bold text-sm leading-snug mb-1.5 text-white">{slot.headline}</p>
        <p className="text-gray-400 text-xs leading-relaxed flex-1 whitespace-pre-line">{slot.body}</p>
        <div className="mt-3">
          <span className="inline-block text-xs font-semibold px-3 py-1.5 rounded-lg" style={{ backgroundColor: `${accent}18`, color: accent, border: `1px solid ${accent}35` }}>
            {slot.cta} →
          </span>
        </div>
      </div>
    </a>
  );
}

function RightAdColumn({ config }) {
  const merged = mergeAdConfig(config);
  const slots = merged.slots;
  return (
    <aside className="fixed right-0 top-0 h-full w-[240px] 2xl:w-[300px] bg-dark-800 border-l border-dark-600 hidden xl:flex flex-col z-20">
      {/* Sponsored label — was unreadable gray-600 before 2026-06-05. Now uses
          the platform amber/gold theme + a subtle filled pill background so the
          label is legible at a glance without competing with the actual ad units
          below. */}
      <div className="h-16 flex items-center justify-center px-4 border-b border-dark-600 flex-shrink-0 bg-gradient-to-b from-amber-500/10 to-transparent">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[10px] font-bold tracking-[0.2em] uppercase">
          Sponsored
        </span>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {slots.map(slot => <SidebarAdUnit key={slot.id} slot={slot} />)}
        <div className="pt-2 pb-1 text-center space-y-1">
          <Link to="/advertise" className="text-[10px] text-violet-400 hover:text-violet-300 transition-colors block font-semibold">
            Advertise with Us →
          </Link>
          <p className="text-[9px] text-gray-700 mt-1">Leaderboard 728×90 · from $500/day<br />Rectangle 300×250 · from $300/day</p>
        </div>
      </div>
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const navGroups = [
  { label: 'Home', groupIcon: LayoutDashboard, standalone: true, href: '/', items: [{ name: 'Dashboard', href: '/', icon: LayoutDashboard }] },
  { label: 'Analytics', groupIcon: BarChart3, items: [
    { name: 'Trade Analytics', href: '/analytics',    icon: BarChart3 },
    { name: 'Quant Desk',      href: '/quant-desk',   icon: Gem },
    { name: 'Risk Management', href: '/risk',         icon: Shield },
    { name: 'Market Trends',   href: '/market-trend', icon: TrendingUp },
  ]},
  { label: 'Intelligence', groupIcon: Brain, items: [
    // Portfolio decision-support
    { name: 'Portfolio Analytics', href: '/portfolio-analytics', icon: BarChart3, subgroup: 'Portfolio' },
    { name: 'Rebalancer',      href: '/rebalancer',      icon: Scale,        subgroup: 'Portfolio', feature: 'portfolioRebalancing' },
    { name: 'Tax Strategy',    href: '/tax-strategy',    icon: Receipt,      subgroup: 'Portfolio', feature: 'portfolioRebalancing' },
    { name: 'Tax Report',      href: '/tax-report',      icon: Receipt,      subgroup: 'Portfolio' },

    // AI signals & forecasting
    { name: 'AI Signals',      href: '/signals',         icon: Zap,          subgroup: 'AI Signals' },
    { name: 'Forecasting',     href: '/forecast',        icon: Brain,        subgroup: 'AI Signals' },
    { name: 'Forecast Accuracy', href: '/forecast-accuracy', icon: Target,   subgroup: 'AI Signals' },
    { name: 'Signal Channels', href: '/signal-channels', icon: Radio,        subgroup: 'AI Signals' },
    { name: 'AI Risk Analyst', href: '/ai-risk-analyst', icon: MessageSquare, subgroup: 'AI Signals', feature: 'aiRiskAnalyst' },

    // Research & data
    { name: 'News Feed',       href: '/news',            icon: Newspaper,    subgroup: 'Research' },
    { name: 'Coin Database',   href: '/coin',            icon: Coins,        subgroup: 'Research' },
  ]},
  { label: 'CEX Trade', groupIcon: TrendingUp, items: [
    // Manual Trading — exchanges & venues you trade on directly
    { name: 'CEX Trading',      href: '/cex-trading',      icon: Activity,     subgroup: 'Manual Trading' },
    { name: 'Futures',          href: '/futures',          icon: Zap,          subgroup: 'Manual Trading', feature: 'cexFutures' },
    { name: 'Smart Trade',      href: '/smart-trade',      icon: Zap,          subgroup: 'Manual Trading', feature: 'smartTradeTerminal' },
    { name: 'Algo Orders',      href: '/algo-orders',      icon: Layers,       subgroup: 'Manual Trading', feature: 'cexFutures' },

    // Automated Bots — set-and-forget strategies
    { name: 'Bots',             href: '/bots',             icon: Bot,          subgroup: 'Automated Bots' },
    { name: 'Grid Bot',         href: '/grid-bot',         icon: Grid3X3,      subgroup: 'Automated Bots', feature: 'gridBot' },
    { name: 'DCA Safety Bot',   href: '/dca-safety',       icon: TrendingDown, subgroup: 'Automated Bots', feature: 'gridBot' },
    { name: 'Conditional Bots', href: '/conditional-bots', icon: GitBranch,    subgroup: 'Automated Bots', feature: 'conditionalBots' },
    { name: 'Arbitrage',        href: '/arbitrage',        icon: Zap,          subgroup: 'Automated Bots', feature: 'arbitrageDetector' },
    { name: 'TradingView Webhooks', href: '/webhooks',     icon: Radio,        subgroup: 'Automated Bots' },

    // Strategy & Backtest — design and validate before going live
    { name: 'Strategy Builder', href: '/strategy-builder', icon: Wrench,       subgroup: 'Strategy & Backtest', feature: 'aiStrategyGen' },
    { name: 'Stress Test',      href: '/stress-test',      icon: Skull,        subgroup: 'Strategy & Backtest', feature: 'realBacktesting' },
    { name: 'Paper Trading',    href: '/paper-trading',    icon: FlaskConical, subgroup: 'Strategy & Backtest' },
    { name: 'Strategy Heatmap', href: '/strategy-heatmap', icon: Grid3X3,      subgroup: 'Strategy & Backtest' },

  ]},
  // ─── DEX Trade ─────────────────────────────────────────────────────────────
  // Isolated DEX surface — every entry here uses the user's connected wallet
  // (no CCXT keys). New DEX automation lives ONLY in this menu so the audited
  // CEX surface above stays untouched. Phase 1-4 DEX bots will be added under
  // 'Automated Bots' as they ship.
  { label: 'DEX Trade', groupIcon: Wallet, items: [
    { name: 'DEX Swap',         href: '/swap',             icon: ArrowDownUp,  subgroup: 'Manual Trading' },
    { name: 'Wallet Sessions',  href: '/wallet-sessions',  icon: ShieldCheck,  subgroup: 'Manual Trading' },
    // Phase 1 — 1inch LOP auto-fill limit orders. Net-new automation surface.
    { name: 'Limit Orders',     href: '/dex-limit-orders', icon: Layers,       subgroup: 'Automated Trading' },
    // Phase 1.5 — recurring scheduled buys (DCA) layered on the same adapters.
    { name: 'DCA',              href: '/dex-dca',          icon: CalendarRange, subgroup: 'Automated Trading' },
    // Phase 2 — Chainlink-triggered stop-loss / take-profit orders.
    { name: 'Stop Orders',      href: '/dex-stop-orders',  icon: Shield,        subgroup: 'Automated Trading' },
    // Phase 3 — Smart Trade: entry + linked TP + linked SL composed in one bundle.
    { name: 'Smart Trade',      href: '/dex-smart-trade',  icon: Zap,           subgroup: 'Automated Trading' },
    // Phase 5 — Algo Orders: TWAP/VWAP/Iceberg one-shot execution across a short window.
    { name: 'Algo Orders',      href: '/dex-algo-orders',  icon: Layers,        subgroup: 'Automated Trading' },
    // Phase 6 — Conditional Bot: regime-switching orchestrator that auto-activates the right inner DEX bot per market regime.
    { name: 'Conditional Bot',  href: '/dex-conditional-bot', icon: GitBranch,  subgroup: 'Automated Trading' },
    // Phase 7 — Arbitrage Bot: cross-DEX spread capture between CoW + Velora with MEV protection + loss-streak auto-pause.
    { name: 'Arbitrage Bot',    href: '/dex-arbitrage',    icon: Zap,           subgroup: 'Automated Trading' },
    // Phase 4a — Grid Bot: buy-low/sell-high across a price range, autonomous via pre-signed pool.
    { name: 'Grid Bot',         href: '/dex-grid-bot',     icon: Grid3X3,       subgroup: 'Automated Trading' },
    // Phase 4b — TradingView Webhooks: signal from any chart → DEX swap via pre-signed pool.
    { name: 'TradingView Webhooks', href: '/dex-tv-webhooks',  icon: Radio,         subgroup: 'Automated Trading' },
  ]},
  { label: 'Discover', groupIcon: Compass, items: [
    // Token Scanner — V7
    { name: 'Token Scanner',    href: '/scanner',          icon: Compass,      subgroup: 'Token Scanner' },
    // Marketplace & Social — find what others have built
    { name: 'Marketplace',      href: '/marketplace',      icon: Store,        subgroup: 'Marketplace & Social', feature: 'fullMarketplace' },
    { name: 'Copy Trading',     href: '/copy-trading',     icon: Copy,         subgroup: 'Marketplace & Social', feature: 'copyTrading' },
    { name: 'Top Traders',      href: '/top-traders',      icon: Trophy,       subgroup: 'Marketplace & Social' },
    { name: 'Verify Track Record', href: '/seller-verify', icon: BadgeCheck,   subgroup: 'Marketplace & Social', feature: 'verifiedTrackRecordSeller' },

    // Tools & Reports — sizing, journaling, integrations
    { name: 'Position Sizing',  href: '/position-sizing',  icon: Calculator,   subgroup: 'Tools & Reports' },
    { name: 'Trading Journal',  href: '/journal',          icon: BookOpen,     subgroup: 'Tools & Reports' },
    { name: 'Outbound Webhooks',href: '/outbound-webhooks',icon: Webhook,      subgroup: 'Tools & Reports' },
  ]},
  { label: 'Elite', groupIcon: Sparkles, badge: 'PRO', items: [
    // Trader's Suite — pro-trader tooling for active CEX + DEX users
    { name: 'Multi-Account Aggregator', href: '/aggregator', icon: Layers, subgroup: "Trader's Suite", feature: 'multiAccountAggregator' },
    { name: 'Tax-Loss Harvest',         href: '/tax-harvest',icon: Receipt, subgroup: "Trader's Suite", feature: 'taxAutoHarvest' },
    { name: 'Institutional Reports',    href: '/reports',    icon: FileText, subgroup: "Trader's Suite", feature: 'institutionalReports' },
    { name: 'AI Strategy Coach',        href: '/strategy-coach', icon: Brain, subgroup: "Trader's Suite", feature: 'aiStrategyCoach' },
    { name: 'Quant Lab',                href: '/quant-lab',  icon: FlaskConical, subgroup: "Trader's Suite", feature: 'quantLab' },
    { name: 'Uniswap V3 LP Studio',     href: '/lp-studio',  icon: Droplets, subgroup: "Trader's Suite", feature: 'uniswapLpStudio' },
    { name: 'Token Launch Scanner',     href: '/token-scanner', icon: ShieldAlert, subgroup: "Trader's Suite", feature: 'tokenLaunchScanner' },

    // Issuer activity & launch tools
    { name: 'DEX Liquidity Optimizer', href: '/dex-liquidity-optimizer', icon: Droplets, subgroup: 'Issuer Tools', feature: 'dexLiquidityOptimizer' },
    { name: 'Listing Readiness',       href: '/listing-readiness',       icon: ClipboardCheck, subgroup: 'Issuer Tools', feature: 'listingReadiness' },
    { name: 'Launch Volume Forecaster', href: '/launch-volume-forecaster', icon: TrendingUp, subgroup: 'Issuer Tools', feature: 'launchVolumeForecaster' },
    { name: 'Launch Roadmap',      href: '/launch-roadmap',      icon: CalendarRange, subgroup: 'Issuer Tools', feature: 'launchRoadmap' },
    { name: 'Volume Health',       href: '/volume-health',       icon: HeartPulse,    subgroup: 'Issuer Tools', feature: 'volumeHealthWidget' },
    { name: 'Competitor Tracker',  href: '/competitor-tracker',  icon: Radar,         subgroup: 'Issuer Tools', feature: 'competitorTracker' },

    // Tokenomics suite
    { name: 'Tokenomics Advisor',  href: '/tokenomics-advisor',  icon: FileText,      subgroup: 'Tokenomics', feature: 'tokenomicsAdvisor' },
    { name: 'Tokenomics Studio',   href: '/tokenomics-studio',   icon: Building2,     subgroup: 'Tokenomics', feature: 'tokenomicsAdvisor' },

    // Pattern-Evasion was an anti-fingerprinting tool designed to defeat
    // wash-trading surveillance (Bubblemaps/Chainalysis/Nansen). Removed
    // 2026-05-20 — selling tools to evade market-manipulation detection is
    // itself a regulatory violation under SEC/CFTC/MiCA and could be charged
    // as aiding-and-abetting. See [[project-removed-wash-trading-features]].
  ]},
  { label: 'Community', groupIcon: MessageSquare, items: [
    { name: 'Live Trade Feed',  href: '/community/live-feed',         icon: Activity },
    { name: 'Official Channels', href: '/community/official-channels', icon: Hash },
    { name: 'Chat Rooms',     href: '/community',        icon: MessageSquare },
    { name: 'My Profile',     href: '/community/profile', icon: Shield },
    { name: 'Leaderboard',    href: '/community/leaderboard', icon: TrendingUp },
    { name: 'Achievements',   href: '/community/achievements', icon: Crown },
    { name: 'Verified Projects', href: '/community/projects', icon: Shield },
    { name: 'Discover Channels', href: '/community/discover', icon: Hash },
    { name: 'My Channel Earnings', href: '/community/my-earnings', icon: DollarSign },
  ]},
  { label: 'Tools', groupIcon: Wrench, items: [
    { name: 'Alerts',       href: '/alerts',       icon: Bell },
    { name: 'Multi-Chain',  href: '/multi-chain',  icon: Globe },
    { name: 'Install App',  href: '/install-app',  icon: Download },
    { name: 'Referral',     href: '/referral',     icon: Gift, feature: 'referralSystem' },
  ]},
];

// ─── User-avatar menu (bottom of rail) ──────────────────────────────────────
//
// Shows the signed-in user's identity at a glance and gates logout behind a
// confirmation popover so an accidental click doesn't end the session.
//
// Pattern follows Stripe / Linear / GitHub: avatar in the rail, click to open
// a small popover showing name + email + tier, with an explicit "Sign out"
// button that requires a second confirm tap.

function UserAvatarMenu({ user, tierCfg, handleLogout }) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setConfirming(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const initials = (() => {
    if (!user) return '?';
    const name = (user.name || '').trim();
    if (name) {
      const parts = name.split(/\s+/);
      return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
    }
    return (user.email || '?')[0].toUpperCase();
  })();

  const tierColorBg = tierCfg.color?.includes('amber') ? 'bg-amber-400'
    : tierCfg.color?.includes('emerald') ? 'bg-emerald-500'
    : tierCfg.color?.includes('blue') ? 'bg-blue-500'
    : tierCfg.color?.includes('violet') ? 'bg-violet-500'
    : tierCfg.color?.includes('cyan') ? 'bg-cyan-500'
    : 'bg-gray-500';

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => { setOpen(o => !o); setConfirming(false); }}
        title={user?.name || user?.email || 'Account'}
        className="w-14 h-10 flex items-center justify-center rounded-xl text-gray-300 hover:text-white hover:bg-dark-700 transition-all duration-150"
      >
        {user?.avatarUrl ? (
          <img src={user.avatarUrl} alt="" className="w-7 h-7 rounded-full" />
        ) : (
          <div className={`w-7 h-7 rounded-full ${tierColorBg} text-dark-900 text-xs font-bold flex items-center justify-center`}>
            {initials}
          </div>
        )}
      </button>

      {open && (
        <div className="fixed bottom-3 left-[80px] z-50 w-64 bg-dark-800 border border-dark-600 rounded-xl shadow-2xl overflow-hidden">
          {/* Identity card */}
          <div className="p-4 border-b border-dark-600">
            <div className="flex items-center gap-3">
              {user?.avatarUrl ? (
                <img src={user.avatarUrl} alt="" className="w-10 h-10 rounded-full" />
              ) : (
                <div className={`w-10 h-10 rounded-full ${tierColorBg} text-dark-900 text-sm font-bold flex items-center justify-center flex-shrink-0`}>
                  {initials}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-white truncate">
                  {user?.name || user?.email?.split('@')[0] || 'User'}
                </div>
                <div className="text-xs text-gray-400 truncate">
                  {user?.email || ''}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3 text-[10px]">
              <span className={`px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${tierCfg.color || 'text-gray-400'} ${tierColorBg.replace('bg-', 'bg-').replace('-400', '-400/15').replace('-500', '-500/15')} border ${tierCfg.color?.replace('text-', 'border-').replace('-400', '-400/30').replace('-500', '-500/30') || 'border-gray-600'}`}>
                {tierCfg.label || 'Starter'}
              </span>
              {user?.role === 'admin' && (
                <span className="px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-400 border border-amber-400/30 font-bold uppercase tracking-wider">
                  Admin
                </span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="p-1">
            <Link
              to="/settings"
              onClick={() => { setOpen(false); setConfirming(false); }}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-dark-700 transition-colors"
            >
              <Settings className="w-4 h-4" />
              <span>Account settings</span>
            </Link>
            <Link
              to="/learning-paths"
              onClick={() => { setOpen(false); setConfirming(false); }}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-dark-700 transition-colors"
            >
              <HelpCircle className="w-4 h-4" />
              <span>Learning paths</span>
            </Link>
            <Link
              to="/help-center"
              onClick={() => { setOpen(false); setConfirming(false); }}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-dark-700 transition-colors"
            >
              <HelpCircle className="w-4 h-4" />
              <span>Help &amp; docs</span>
            </Link>
            <Link
              to="/learning-library"
              onClick={() => { setOpen(false); setConfirming(false); }}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-dark-700 transition-colors"
            >
              <HelpCircle className="w-4 h-4" />
              <span>Learning library</span>
            </Link>

            <div className="h-px bg-dark-600 my-1" />

            {!confirming ? (
              <button
                onClick={() => setConfirming(true)}
                className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-gray-400 hover:text-rose-400 hover:bg-dark-700 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Sign out</span>
              </button>
            ) : (
              <div className="px-3 py-2">
                <p className="text-xs text-gray-400 mb-2">Sign out of haythix?</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setOpen(false); handleLogout(); }}
                    className="flex-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-500 hover:bg-rose-400 text-white transition-colors"
                  >
                    Yes, sign out
                  </button>
                  <button
                    onClick={() => setConfirming(false)}
                    className="flex-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-dark-700 hover:bg-dark-600 text-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sidebar Rail + Flyout Panel Component ──────────────────────────────────

function SidebarRail({ navGroups, user, tierCfg, location, sidebarOpen, setSidebarOpen, setLockedFeature, handleLogout }) {
  const [hoveredGroup, setHoveredGroup] = useState(null);
  const hoverTimer = useRef(null);
  const closeTimer = useRef(null);

  // Which group contains the active route?
  const activeGroupLabel = navGroups.find(g =>
    g.items.some(i => i.href === location.pathname || (i.href !== '/' && location.pathname.startsWith(i.href + '/')))
  )?.label || null;

  const openGroup = hoveredGroup;
  const activeGroup = navGroups.find(g => g.label === openGroup && !g.standalone);

  const openPanel = (label) => {
    clearTimeout(closeTimer.current);
    hoverTimer.current = setTimeout(() => setHoveredGroup(label), 80);
  };
  const closePanel = () => {
    clearTimeout(hoverTimer.current);
    closeTimer.current = setTimeout(() => setHoveredGroup(null), 200);
  };
  const keepPanel = () => clearTimeout(closeTimer.current);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setHoveredGroup(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Close on click outside
  useEffect(() => {
    if (!openGroup) return;
    const onClick = (e) => {
      if (!e.target.closest('.sidebar-rail') && !e.target.closest('.sidebar-flyout')) {
        setHoveredGroup(null);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [openGroup]);

  const bottomItems = [
    { name: 'Settings', href: '/settings', icon: Settings, show: true, warning: user && !user.mfaEnabled },
    {
      name: 'Admin Center', href: '/admin', icon: Crown,
      // Show for the legacy Owner AND for any user with at least one RBAC
      // role assigned (CTO, GM, Director, Manager, etc.).
      show: user?.role === 'admin' || (Array.isArray(user?.roles) && user.roles.length > 0),
    },
    { name: 'Help', href: '/help', icon: HelpCircle, show: true },
  ].filter(i => i.show);

  return (
    <>
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Rail */}
      <aside className={`
        sidebar-rail
        fixed top-0 left-0 z-50 h-full w-[72px] bg-dark-800 border-r border-dark-600
        flex flex-col items-center
        lg:translate-x-0 transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <Link to="/" className="h-16 w-full flex items-center justify-center border-b border-dark-600 flex-shrink-0">
          <div className="relative">
            <img src={logo} alt="haythix" className="w-9 h-9" />
            <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-dark-800"
              style={{ backgroundColor: tierCfg.color?.replace('text-', '').includes('amber') ? '#f59e0b' : tierCfg.color?.includes('emerald') ? '#10b981' : tierCfg.color?.includes('blue') ? '#3b82f6' : tierCfg.color?.includes('violet') ? '#8b5cf6' : tierCfg.color?.includes('cyan') ? '#06b6d4' : '#6b7280' }} />
          </div>
        </Link>

        {/* Group icons */}
        <nav className="flex-1 flex flex-col items-center py-3 gap-1 w-full">
          {navGroups.map((group) => {
            const GIcon = group.groupIcon;
            const isActiveGroup = activeGroupLabel === group.label;
            const isOpen = openGroup === group.label;

            // Standalone items (Home) — just a link
            if (group.standalone) {
              const isActive = location.pathname === group.href;
              return (
                <Link key={group.label} to={group.href} title={group.label}
                  onClick={() => { setSidebarOpen(false); setHoveredGroup(null); }}
                  className={`relative w-14 h-14 flex flex-col items-center justify-center rounded-xl transition-all duration-150
                    ${isActive ? 'bg-accent-primary/15 text-accent-primary' : 'text-gray-500 hover:text-white hover:bg-dark-700'}`}>
                  <GIcon className="w-5 h-5" />
                  <span className="text-[9px] mt-0.5 font-medium">{group.label}</span>
                  {isActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-accent-primary rounded-r-full" />}
                </Link>
              );
            }

            // Group items — hover to open flyout
            return (
              <button key={group.label} title={group.label}
                onMouseEnter={() => openPanel(group.label)}
                onMouseLeave={closePanel}
                onClick={() => setHoveredGroup(prev => prev === group.label ? null : group.label)}
                className={`relative w-14 h-14 flex flex-col items-center justify-center rounded-xl transition-all duration-150
                  ${isOpen ? 'bg-accent-primary/12 text-accent-primary' : isActiveGroup ? 'bg-accent-primary/10 text-accent-primary' : 'text-gray-500 hover:text-white hover:bg-dark-700'}`}>
                <GIcon className="w-5 h-5" />
                <span className="text-[9px] mt-0.5 font-medium">{group.label}</span>
                {group.badge && (
                  <span className="absolute top-1 right-1 text-[7px] font-bold px-1 py-px rounded bg-gradient-to-r from-amber-400 to-orange-400 text-dark-900 leading-none">
                    {group.badge}
                  </span>
                )}
                {isActiveGroup && !isOpen && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-accent-primary rounded-r-full" />}
                {isOpen && <span className="absolute right-0 top-1/2 -translate-y-1/2 w-[3px] h-4 bg-accent-primary rounded-l-full" />}
              </button>
            );
          })}
        </nav>

        {/* Bottom items */}
        <div className="flex flex-col items-center gap-1 py-3 border-t border-dark-600 w-full">
          {bottomItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link key={item.name} to={item.href} title={item.name}
                onClick={() => setSidebarOpen(false)}
                className={`relative w-14 h-10 flex items-center justify-center rounded-xl transition-all duration-150
                  ${isActive ? 'bg-accent-primary/15 text-accent-primary' : 'text-gray-500 hover:text-white hover:bg-dark-700'}`}>
                <div className="relative">
                  <item.icon className="w-5 h-5" />
                  {item.warning && <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-400" />}
                </div>
              </Link>
            );
          })}
          <UserAvatarMenu user={user} tierCfg={tierCfg} handleLogout={handleLogout} />
        </div>
      </aside>

      {/* Flyout panel — opens to the right of the rail on hover */}
      {activeGroup && (
        <div className="sidebar-flyout fixed top-0 left-[72px] bottom-0 w-64 bg-dark-900 border-r border-dark-600 z-40
          animate-[slideIn_0.15s_ease-out]"
          onMouseEnter={keepPanel}
          onMouseLeave={closePanel}
        >
          <style>{`
            @keyframes slideIn { from { opacity: 0; transform: translateX(-8px); } to { opacity: 1; transform: translateX(0); } }
            .nav-scroll::-webkit-scrollbar { width: 6px; }
            .nav-scroll::-webkit-scrollbar-track { background: transparent; }
            .nav-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.06); border-radius: 3px; }
            .nav-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.12); }
          `}</style>

          {/* Panel header */}
          <div className="h-16 flex items-center gap-2 px-5 border-b border-dark-600">
            <activeGroup.groupIcon className="w-5 h-5 text-accent-primary" />
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">{activeGroup.label}</h3>
            {activeGroup.badge && (
              <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-gradient-to-r from-amber-400 to-orange-400 text-dark-900">
                {activeGroup.badge}
              </span>
            )}
          </div>

          {/* Panel items — grouped by subgroup with visible dividers */}
          <div className="nav-scroll py-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 4rem)' }}>
            {/* Most-specific href in this group wins as active. Without
                this, a parent route like `/community` would also "match"
                when the user is on a child like `/community/live-feed`,
                highlighting both items. */}
            {(() => {
              const activeHref = activeGroup.items
                .map(i => i.href)
                .filter(h => location.pathname === h || (h !== '/' && location.pathname.startsWith(h + '/')))
                .sort((a, b) => b.length - a.length)[0];
              return activeGroup.items.map((item, idx) => {
              const isActive = item.href === activeHref;
              const isLocked = item.feature && user && user.role !== 'admin' && !userHasFeature(user.tier, item.feature);
              const prevSub = idx > 0 ? activeGroup.items[idx - 1].subgroup : null;
              const showSubHeader = item.subgroup && item.subgroup !== prevSub;
              const isFirstSub = showSubHeader && idx === 0;

              const itemNode = isLocked ? (
                <button key={item.name}
                  onClick={() => { setLockedFeature(item.feature); setHoveredGroup(null); setSidebarOpen(false); }}
                  className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-[13px] text-gray-600 hover:text-gray-400 hover:bg-dark-800 transition-all">
                  <item.icon className="w-4 h-4 opacity-40 flex-shrink-0" />
                  <span className="flex-1 text-left">{item.name}</span>
                  <Lock className="w-3.5 h-3.5 text-gray-700" />
                </button>
              ) : (
                <Link key={item.name} to={item.href}
                  onClick={() => { setHoveredGroup(null); setSidebarOpen(false); }}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] transition-all duration-150
                    ${isActive ? 'bg-accent-primary/15 text-accent-primary font-medium' : 'text-gray-400 hover:text-white hover:bg-dark-800'}`}>
                  <item.icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-accent-primary' : ''}`} />
                  <span className="flex-1">{item.name}</span>
                  {item.badge && (
                    <span className="text-[8px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30 font-mono">
                      {item.badge}
                    </span>
                  )}
                  <ChevronRight className={`w-3.5 h-3.5 transition-opacity ${isActive ? 'opacity-60' : 'opacity-0 group-hover:opacity-40'}`} />
                </Link>
              );

              if (!showSubHeader) {
                return <div key={item.name} className="px-3">{itemNode}</div>;
              }

              return (
                <div key={`section-${item.name}`} className={isFirstSub ? '' : 'mt-2 pt-3 border-t border-dark-600'}>
                  <div className="flex items-center gap-2 px-5 pb-1.5">
                    <span className="text-[10px] font-bold tracking-[0.15em] uppercase text-accent-primary/80">
                      {item.subgroup}
                    </span>
                    <div className="flex-1 h-px bg-gradient-to-r from-accent-primary/20 to-transparent" />
                  </div>
                  <div className="px-3">{itemNode}</div>
                </div>
              );
            });
            })()}
          </div>
        </div>
      )}
    </>
  );
}

const footerLinks = [
  { name: 'Terms', href: '/terms' },
  { name: 'Privacy', href: '/privacy' },
  { name: 'Pricing', href: '/pricing' },
];

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adConfig, setAdConfig] = useState(null);
  const [subStatus, setSubStatus] = useState(null);
  const [lockedFeature, setLockedFeature] = useState(null);
  const [mfaBannerDismissed, setMfaBannerDismissed] = useState(
    () => sessionStorage.getItem('mfaNudgeDismissed') === '1'
  );
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const tierCfg = getTierConfig(user?.tier || 'starter');

  const showMfaNudge = user && !user.mfaEnabled && !mfaBannerDismissed;

  function dismissMfaNudge() {
    sessionStorage.setItem('mfaNudgeDismissed', '1');
    setMfaBannerDismissed(true);
  }

  // Trial countdown — recompute on render (no need for a timer; page navigations refresh it)
  const trialDays = user?.tier === 'trial' ? trialDaysRemaining(user?.trialEndsAt) : null;
  const showTrialBanner = trialDays !== null && trialDays >= 0;
  const trialBannerColor = trialDays <= 1
    ? { bar: 'bg-red-500/10 border-red-500/30', icon: 'text-red-400', text: 'text-red-200', sub: 'text-red-300', link: 'text-red-300 hover:text-red-100' }
    : trialDays <= 3
    ? { bar: 'bg-orange-500/10 border-orange-500/30', icon: 'text-orange-400', text: 'text-orange-200', sub: 'text-orange-300', link: 'text-orange-300 hover:text-orange-100' }
    : { bar: 'bg-emerald-500/10 border-emerald-500/30', icon: 'text-emerald-400', text: 'text-emerald-200', sub: 'text-emerald-300', link: 'text-emerald-300 hover:text-emerald-100' };

  useEffect(() => {
    getAdConfig()
      .then(data => setAdConfig(data))
      .catch(() => {});
    if (user) {
      getSubscriptionStatus()
        .then(data => setSubStatus(data))
        .catch(() => {});
    }
  }, [user?.userId]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Left Sidebar — Icon rail + flyout panel */}
      <SidebarRail
        navGroups={navGroups}
        user={user}
        tierCfg={tierCfg}
        location={location}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        setLockedFeature={setLockedFeature}
        handleLogout={handleLogout}
      />

      {/* Upgrade modal for locked features */}
      {lockedFeature && (
        <UpgradeModal featureKey={lockedFeature} onClose={() => setLockedFeature(null)} />
      )}

      {/* Right Ad Column — visible from xl (1280px+) at NARROWER 240px so iPad
          Pro 12.9" landscape (1366px) + most laptops still see the ad inventory
          without crowding the main content. At 2xl+ (1536px) it widens to the
          full 300px. The earlier 2xl-only gate left iPads ad-less; user
          reported the missing rail on 2026-06-05. */}
      <RightAdColumn config={adConfig} />

      {/* Main content — offset for left rail + responsive right rail width */}
      <div className="lg:pl-[72px] xl:pr-[240px] 2xl:pr-[300px]">
        {/* Sticky header + ticker */}
        <div className="sticky top-0 z-30">
          <header className="h-16 bg-dark-800 border-b border-dark-600">
            <div className="flex items-center h-full px-4 gap-3">
              {/* Mobile hamburger */}
              <button
                className="lg:hidden p-2 text-gray-400 hover:text-white flex-shrink-0"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </button>

              {/* Center — header ad banner (desktop only) */}
              <HeaderAdBanner config={adConfig} />

              {/* Right — Notification bell + Currency selector + System Online */}
              <div className="flex items-center gap-3 ml-auto flex-shrink-0">
                <NotificationBell />
                <CurrencySelector />
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-success opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-accent-success" />
                  </span>
                  <span className="text-sm text-gray-400 hidden sm:inline">System Online</span>
                </div>
              </div>
            </div>
          </header>

          {/* Live price ticker */}
          <PriceTicker />
          {/* Offline indicator */}
          <OfflineBanner />
        </div>

        {/* Trial countdown banner */}
        {showTrialBanner && (
          <div className={`mx-4 md:mx-6 lg:mx-8 mt-4 flex items-center gap-3 px-4 py-3 rounded-xl border ${trialBannerColor.bar}`}>
            <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${trialBannerColor.icon}`} />
            <p className={`text-sm flex-1 ${trialBannerColor.sub}`}>
              <span className={`font-semibold ${trialBannerColor.text}`}>
                {trialDays === 0 ? 'Trial expires today — ' : `${trialDays} day${trialDays === 1 ? '' : 's'} left in your trial — `}
              </span>
              {trialDays <= 3
                ? <>Your card will be charged <strong className={trialBannerColor.text}>$29</strong> when the trial ends. Cancel now in the </>
                : <>Auto-converts to Standard ($29/mo) when the trial ends. Manage in </>
              }
              <Link to="/settings?tab=subscription" className={`underline underline-offset-2 font-medium ${trialBannerColor.link}`}>
                Subscription settings →
              </Link>
            </p>
          </div>
        )}

        {/* MFA nudge banner */}
        {showMfaNudge && (
          <div className="mx-4 md:mx-6 lg:mx-8 mt-4 flex items-center gap-3 px-4 py-3 rounded-xl
                          bg-amber-500/10 border border-amber-500/30 text-amber-300">
            <ShieldAlert className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <p className="text-sm flex-1">
              <span className="font-semibold text-amber-200">Secure your account — </span>
              Enable two-factor authentication (TOTP) to protect against unauthorised access.{' '}
              <Link
                to="/settings?tab=security"
                className="underline underline-offset-2 hover:text-amber-100 transition-colors font-medium"
              >
                Enable MFA now →
              </Link>
            </p>
            <button
              onClick={dismissMfaNudge}
              aria-label="Dismiss"
              className="text-amber-500 hover:text-amber-300 transition-colors flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Trial-fraud-blocked banner — shown when a duplicate card was used to start a trial */}
        {subStatus?.status === 'trial_fraud_blocked' && (
          <div className="mx-4 md:mx-6 lg:mx-8 mt-4 flex items-start gap-3 px-4 py-4 rounded-xl
                          bg-violet-500/10 border border-violet-500/30">
            <ShieldAlert className="w-5 h-5 text-violet-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-violet-200 mb-0.5">
                Your trial couldn't be activated
              </p>
              <p className="text-sm text-violet-300">
                This payment method was previously used for a haythix trial. No worries — you
                can subscribe directly and get <span className="font-semibold text-white">full
                access immediately</span>, no trial period required.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                             bg-violet-500/20 text-violet-200 border border-violet-500/40
                             hover:bg-violet-500/30 transition-colors"
                >
                  View all plans →
                </Link>
                <Link
                  to="/settings?tab=subscription"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                             bg-dark-700 text-gray-300 border border-dark-500
                             hover:bg-dark-600 transition-colors"
                >
                  Subscribe in Settings
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Page content */}
        <main className="p-4 md:p-6 lg:p-8 min-h-[calc(100vh-4rem-120px)]">
          {children}
        </main>

        {/* No footer inside the logged-in app shell — sidebar nav covers
            wayfinding and a marketing footer below dashboard content reads as
            cluttered. Reverted 2026-06-06 after the in-app PublicFooter
            looked out of place against the trading UI. Public marketing
            pages still ship PublicFooter; this is app-shell only. */}
      </div>

      {/* PWA install prompt */}
      <InstallPrompt />
    </div>
  );
}
